<svelte:component this={Entry} />

<Loading />

<svelte:head>
  {#if showIcons}
    <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
  {/if}
</svelte:head>

<script>
  import { onMount, setContext } from 'svelte'
  import Loading from '@/components/Loading'

  export let Entry

  // We expect the client and server entry points to pass in an object that contains all shared platform data
  export let platform

  // We set the platform on the context here since this component is the parent of all other components
  // This way any child component can access the platform object when it needs to
  setContext('platform', platform)

  const { store } = platform
  let showIcons = false

  $: if ($store.auth.claims || ($store.icons && $store.icons.material)) {
    showIcons = platform.api.core.isLoggedIn() || ($store.icons && $store.icons.material)
  }

  function informOfAbTest () {
    if (!IS_BROWSER) {
      return
    }

    if (platform.api.core.isLoggedIn()) {
      return
    }

    if ($store.render.abPath === '') {
      return
    }

    // Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'ab_page_view',
        experiment: $store.render.abTest.name,
        variant: $store.render.abPath,
      })
    }

    // Microsoft Clarity
    if (window.clarity) {
      window.clarity('set', 'experiment', $store.render.abTest.name)
      window.clarity('set', 'variant', $store.render.abPath)
    }
  }

  function getRedirectString (dst, urlMap) {
    if (urlMap.destination.startsWith('http')) {
      return urlMap.destination
    }

    const url = new URL('https://' + platform.router.host + urlMap.destination)

    for (const [key, value] of dst.params.entries()) {
      url.searchParams.append(key, value)
    }

    return url.pathname + url.search
  }

  platform.router.before(async (dst) => {
    if (
      !dst.pathname.startsWith('/cms/') &&
      !dst.pathname.startsWith('/iframe/')
    ) {
      try {
        const urlMaps = await platform.api.cms.urlMaps.findAllPages({ extract: true })

        for (const urlMap of urlMaps) {
          if (urlMap.useSourceRegex) {
            const r = new RegExp(urlMap.source)

            if (r.test(dst.pathname)) {
              return { redirect: getRedirectString(dst, urlMap) }
            }
          } else if (urlMap.source === dst.pathname) {
            return { redirect: getRedirectString(dst, urlMap) }
          }
        }
      } catch (err) {}
    }

    // Redirects for ecom url update
    if (
      !dst.pathname.startsWith('/api/') &&
      !dst.pathname.startsWith('/static/') &&
      !dst.pathname.startsWith('/files/') &&
      !dst.pathname.startsWith('/cms/') &&
      !dst.pathname.startsWith('/iframe/')
    ) {
      if (
        dst.params.get('p') ||
        dst.params.get('c') ||
        dst.params.get('b') ||
        dst.params.get('v') ||
        dst.params.get('t')
      ) {
        const product = dst.params.get('p') || dst.params.get('product')
        const category = dst.params.get('c') || dst.params.get('category')
        const brand = dst.params.get('b') || dst.params.get('brand')
        const variant = dst.params.get('v') || dst.params.get('variant')

        const url = new URL('https://' + platform.router.host + dst.pathname)

        if (product && dst.params.get('p')) {
          url.searchParams.append('product', product)
        }

        if (category && dst.params.get('c')) {
          url.searchParams.append('category', category)
        }

        if (brand && dst.params.get('b')) {
          url.searchParams.append('brand', brand)
        }

        if (variant && dst.params.get('v')) {
          url.searchParams.append('variant', variant)
        }

        const urlDecoded = decodeURIComponent(url.pathname + url.search)

        return { redirect: urlDecoded }
      }
    }

    return await platform.api.core.render.isAccessible(dst.pathname)
  })

  platform.router.after(async ({ pathname }) => {
    await platform.api.core.render.load(pathname)
    Entry = await platform.api.core.selectEntry(platform.storeData.config.site.routes)

    informOfAbTest()
  })

  onMount(() => {
    informOfAbTest()
  })
</script>

<noscript>
  <style>
    :global([data-animation-names]) {
      opacity: 1!important;
    }
  
</style>
</noscript>

<style lang="scss">:global(html),
:global(body) {
  margin: 0;
  padding: 0;
  font: 14px sans-serif;
  line-height: normal;
  background: #fff;
  color: #333;
}
:global(html) {
  box-sizing: border-box;
  overflow-x: hidden;
  scroll-behavior: smooth;
}
:global(*),
:global(*:before),
:global(*:after) {
  box-sizing: inherit;
}
:global(img) {
  border: none;
  max-width: 100%;
  height: auto;
}
:global(table) {
  border-spacing: 0;
  position: relative;
}
:global(.cms-disable-scroll) {
  -webkit-overflow-scrolling: touch;
  overflow: hidden;
}
:global(.error-text) {
  color: red;
}</style>
<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};</script>